<template>
  <div :dir="isRtl != false ? 'rtl' : 'ltr'" :class="['relative w-full llm-selector-dropdown-container mb-4']">
    <button 
      type="button" 
      class="btn w-full justify-start font-normal text-left h-auto py-2 px-3 min-h-0"
      :class="props.locked === true ? 'cursor-not-allowed' : ''"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-disabled="props.locked === true"
      :title="props.locked === true ? t('llmSelector.lockedTooltip', 'This option is locked for your plan') : ''"
    >
      <div v-if="selectedModelObject !== null" class="flex items-center selected-llm-head w-full">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0">
          <img v-if="selectedModelObject.iconType === 'asset'" :src="selectedModelObject.icon" :alt="selectedModelObject.displayName" class="w-5 h-5 object-contain" />
          <Icon v-else :icon="selectedModelObject.icon" class="w-4 h-4 text-base-content" />
        </span>
        <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
          <span class="block text-sm font-medium relative w-full whitespace-normal break-words" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">{{ selectedModelObject.displayName }}</span>
          <span class="block text-xs text-base-content opacity-70 whitespace-normal break-words" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">{{ selectedModelObject.description }}</span>
        </div>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60 flex-shrink-0 ml-auto" />
      </div>
      <div v-else class="flex items-center justify-between w-full">
        <span class="text-sm text-base-content opacity-70">{{ t('llmSelector.selectModelPlaceholder') }}</span>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60" />
      </div>
    </button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div 
        v-if="isOpen === true"
        class="absolute z-10 mt-1 w-full bg-base-100 dark:bg-gray-800 shadow-lg rounded-md border border-base-300 dark:border-gray-700 llm-options-list"
        ref="dropdownListRef"
      >
        <div class="p-2 border-b border-base-300 dark:border-gray-700 sticky top-0 bg-base-100 dark:bg-gray-800 z-10">
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="t('llmSelector.searchPlaceholder')" 
            class="input input-sm input-bordered w-full"
            @click.stop
          />
        </div>
        <div class="py-1 space-y-1">
          <div 
            v-for="model in filteredModels" 
            :key="model.__key !== undefined ? model.__key : model.id"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{ 
              'bg-primary text-primary-content': isModelSelected(model) === true && isModelEnabled(model) === true,
              'hover:bg-base-200 dark:hover:bg-gray-700': isModelSelected(model) !== true && isModelEnabled(model) === true,
              'opacity-50 cursor-not-allowed': isModelEnabled(model) !== true
            }"
            @click="isModelEnabled(model) === true ? selectModelAndClose(model.id) : null"
            :title="isModelEnabled(model) !== true ? t('llmSelector.noAccessTooltip', 'You do not have access to this model') : ''"
            :aria-disabled="isModelEnabled(model) !== true"
          >
            <div class="p-3">
              <div class="flex items-start llm-option-row">
                <div class="flex-shrink-0">
                  <span class="inline-flex justify-center items-center w-8 h-8 rounded-lg bg-base-300 dark:bg-gray-600">
                    <img v-if="model.iconType === 'asset'" :src="model.icon" :alt="model.displayName" class="w-6 h-6 object-contain" :class="isModelEnabled(model) !== true ? 'opacity-50' : ''" />
                    <Icon v-else :icon="model.icon" class="w-5 h-5" :class="isModelEnabled(model) !== true ? 'text-base-content opacity-50' : (isModelSelected(model) === true ? 'text-primary-content' : 'text-base-content')" />
                  </span>
                </div>
                <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'">
                  <h3 class="text-sm font-semibold leading-tight" :class="[isModelEnabled(model) !== true ? 'text-base-content opacity-60' : (isModelSelected(model) === true ? 'text-primary-content' : ''), isRtl === true ? 'text-right' : 'text-left']">{{ model.displayName }}</h3>
                  <p class="text-xs mt-0.5 whitespace-normal break-words" :class="[isModelEnabled(model) !== true ? 'text-base-content opacity-50' : (isModelSelected(model) === true ? 'text-primary-content opacity-80' : 'text-base-content opacity-70'), isRtl === true ? 'text-right' : 'text-left']">
                    {{ model.description }}
                  </p>
                  <div class="mt-1.5 pt-1.5 border-t border-base-300 dark:border-gray-600" :class="{'border-primary-content opacity-50': isModelSelected(model) === true && isModelEnabled(model) === true}">
                    <dl class="grid grid-cols-2 text-xs grid-space-x-2 grid-space-y-0_5">
                      <div v-for="feature in model.features" :key="feature.name" class="flex justify-between">
                        <dt :class="isModelEnabled(model) !== true ? 'text-base-content opacity-40' : (isModelSelected(model) === true ? 'text-primary-content opacity-80' : 'text-base-content opacity-60')">{{ feature.name }}:</dt>
                        <dd :class="isModelEnabled(model) !== true ? 'text-base-content opacity-50' : (isModelSelected(model) === true ? 'text-primary-content font-medium' : 'text-base-content font-medium')">{{ feature.value }}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="filteredModels.length === 0" class="text-center py-4 text-sm text-base-content opacity-60">
          <span v-if="availableLLMModels.length > 0">{{ t('llmSelector.noModelsFound') }}</span>
          <span v-else>{{ t('llmSelector.loadingModels') }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from "@iconify/vue/dist/offline";
import { useI18n } from '@/i18n';

const props = defineProps({
  availableLLMModels: {
    type: Array,
    required: true,
    default: () => []
  },
  selectedLLMModel: {
    type: String,
    default: null
  },
  locked: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:selectedLLMModel']);
const isOpen = ref(false);
const dropdownListRef = ref(null); 
const searchQuery = ref('');
const { t, currentLanguage } = useI18n();
const isRtl = computed(() => (['he','ar'].includes(currentLanguage.value) === true) ? true : false);

function isModelEnabled(model) {
  if (model === undefined || model === null) return false;
  const hasAccess = typeof model.hasAccess === 'boolean' ? model.hasAccess : null;
  if (hasAccess !== null) {
    return hasAccess === true ? true : false;
  }
  return true;
}

function getModelDisplayName(model) {
  if (model === undefined || model === null) return t('llmSelector.unknownModel');
  const providerName = (model.provider !== undefined && model.provider !== null && model.provider !== '') ? model.provider : 'N/A';
  const modelId = (model.id !== undefined && model.id !== null && model.id !== '') ? String(model.id) : '';
  const variant = (model.productVariant !== undefined && model.productVariant !== null && model.productVariant !== '') ? model.productVariant : (modelId !== '' ? modelId : 'N/A');
  const version = (model.productVersion !== undefined && model.productVersion !== null && model.productVersion !== '') ? model.productVersion : '';
  return `${providerName} - ${variant} ${version}`.trim();
}

const llmSelectableModels = computed(() => {
  return props.availableLLMModels.filter(model => {
    const product = typeof model.product === 'string' ? model.product.toLowerCase() : '';
    const variant = typeof model.productVariant === 'string' ? model.productVariant.toLowerCase() : '';
    const id = typeof model.id === 'string' ? model.id.toLowerCase() : '';
    if (product.includes('embedd') === true) return false;
    if (variant.includes('embedd') === true) return false;
    if (id.includes('embedd') === true) return false;
    return true;
  });
});

const autoLlmModelId = 'auto-llm';
function isAutoModel(model) {
  if (model === undefined || model === null) return false;
  if (model.isAuto === true) return true;
  return typeof model.id === 'string' && model.id === autoLlmModelId;
}

const enrichedModels = computed(() => {
  const mapped = llmSelectableModels.value.map(model => {
    let description = t('llmSelector.descriptions.generalPurpose');
    let icon = 'carbon:model-alt';
    let features = [
      { name: t('llmSelector.features.toolSupport'), value: t('llmSelector.features.yes') },
      { name: t('llmSelector.features.voiceAgent'), value: t('llmSelector.features.yes') },
    ];
    let speed = t('llmSelector.features.speedValues.medium');
    let performance = t('llmSelector.features.speedValues.good');
    const autoModel = isAutoModel(model);

    const modelVariantLower = model.productVariant !== undefined && model.productVariant !== null ? model.productVariant.toLowerCase() : '';
    const modelIdLower = model.id !== undefined && model.id !== null ? String(model.id).toLowerCase() : '';
    const providerLower = model.provider !== undefined && model.provider !== null ? model.provider.toLowerCase() : '';
    const providerName = model.provider !== undefined && model.provider !== null ? model.provider : '';
    
    if (autoModel === true) {
      const configuredAutoDescription = typeof model.description === 'string' && model.description.trim().length > 0 ? model.description.trim() : null;
      description = configuredAutoDescription !== null ? configuredAutoDescription : t('llmSelector.autoDescription', 'The platform will choose the best AI (LLM) for your agent');
      icon = 'mdi:robot-happy-outline';
      features = [
        { name: t('llmSelector.features.performance'), value: t('llmSelector.features.speedValues.good') },
        { name: t('llmSelector.features.speed'), value: t('llmSelector.features.speedValues.medium') },
        { name: t('llmSelector.features.toolSupport'), value: t('llmSelector.features.yes') },
        { name: t('llmSelector.features.voiceAgent'), value: t('llmSelector.features.yes') }
      ];
      speed = t('llmSelector.features.speedValues.medium');
      performance = t('llmSelector.features.speedValues.good');
    } else {
      if (providerLower === 'azure') {
        icon = 'logos:microsoft-azure';
      }
      else if (providerLower === 'auto') {
        icon = 'carbon:magic-wand';
      } else if (providerLower === 'googlecloud' || providerLower === 'google') {
        icon = 'logos:google-cloud';
      } else if (providerLower === 'meta') {
        icon = 'logos:meta-icon';
      } else if (providerLower === 'aws') {
        icon = 'logos:aws';
      } else if (providerLower === 'openai') {
        icon = 'logos:openai-icon';
      } else if (providerLower === 'groq') {
        icon = '/groq.svg';
      }

      if (
        modelVariantLower.includes('gpt-5.2') === true ||
        modelVariantLower.includes('gpt52') === true ||
        modelVariantLower.includes('gpt.5.2') === true ||
        modelIdLower.includes('gpt-5.2') === true ||
        modelIdLower.includes('gpt52') === true ||
        modelIdLower.includes('gpt.5.2') === true ||
        modelVariantLower.includes('gpt-5.1') === true ||
        modelVariantLower.includes('gpt51') === true ||
        modelVariantLower.includes('gpt.5.1') === true ||
        modelIdLower.includes('gpt-5.1') === true ||
        modelIdLower.includes('gpt51') === true ||
        modelIdLower.includes('gpt.5.1') === true
      ) {
        description = t('llmSelector.descriptions.advancedComplexTasks');
        speed = t('llmSelector.features.speedValues.mediumFast');
        performance = t('llmSelector.features.speedValues.excellent');
      } else if (modelVariantLower.includes('gpt-oss-120b') === true) {
        description = t('llmSelector.descriptions.advancedComplexTasks');
        speed = t('llmSelector.features.speedValues.fast');
        performance = t('llmSelector.features.speedValues.excellent');
      } else if (modelVariantLower.includes('gpt-5-chat') === true) {
        description = t('llmSelector.descriptions.advancedComplexTasks');
        speed = t('llmSelector.features.speedValues.fast');
        performance = t('llmSelector.features.speedValues.excellent');
      } else if ((modelVariantLower.includes('gpt-5') === true || modelVariantLower.includes('gpt5') === true) && modelVariantLower.includes('mini') === false) {
        description = t('llmSelector.descriptions.advancedComplexTasks');
        speed = t('llmSelector.features.speedValues.fast');
        performance = t('llmSelector.features.speedValues.excellent');
      } else if (modelVariantLower.includes('gpt-4') === true || modelVariantLower.includes('gpt4') === true) {
        description = t('llmSelector.descriptions.advancedComplexTasks');
        if (modelVariantLower.includes('turbo') === true || modelVariantLower.includes('o') === true) {
          speed = t('llmSelector.features.speedValues.fast');
        } else {
          speed = t('llmSelector.features.speedValues.medium');
        }
        performance = t('llmSelector.features.speedValues.excellent');
        if (modelVariantLower.includes('vision') === true) {
          description = t('llmSelector.descriptions.advancedVision');
          features.push({ name: t('llmSelector.features.vision'), value: t('llmSelector.features.yes') });
        }
        if (modelVariantLower.includes('gpt-4.1') === true) {
          description = t('llmSelector.descriptions.cuttingEdgeGPT4_1');
          speed = t('llmSelector.features.speedValues.mediumFast');
          performance = t('llmSelector.features.speedValues.excellent');
        }
      } else if (modelVariantLower.includes('gpt-3.5') === true || modelVariantLower.includes('gpt35') === true) {
        description = t('llmSelector.descriptions.capableBalanceSpeedPerformance');
        speed = t('llmSelector.features.speedValues.fast');
        performance = t('llmSelector.features.speedValues.good');
      } else if (modelVariantLower.includes('gpt4o-mini') === true || modelVariantLower.includes('o3-mini') === true || modelVariantLower.includes('gpt.4.1-mini') === true || modelVariantLower.includes('gpt-4.1-mini') === true || modelVariantLower.includes('gpt4.1-mini') === true || modelVariantLower.includes('gpt-5-mini') === true) {
        description = t('llmSelector.descriptions.compactEfficientGPT4');
        speed = t('llmSelector.features.speedValues.veryFast');
        performance = t('llmSelector.features.speedValues.good');
      } else if (modelVariantLower.includes('gpt4o') === true || modelVariantLower.includes('o3') === true || modelVariantLower.includes('gpt4.1') === true) {
        description = t('llmSelector.descriptions.omnimodalTextVisionAudio');
        speed = t('llmSelector.features.speedValues.fast');
        performance = t('llmSelector.features.speedValues.veryGood');
        features.push({ name: t('llmSelector.features.multimodal'), value: t('llmSelector.features.yes') });
      } else if (modelVariantLower.includes('nano') === true) {
        description = t('llmSelector.descriptions.lightweightFast');
        speed = t('llmSelector.features.speedValues.blazingFast');
        performance = t('llmSelector.features.speedValues.fair');
      } else if (modelVariantLower.includes('llama3') === true || modelVariantLower.includes('llama-3') === true) {
        description = t('llmSelector.descriptions.metaPowerfulEfficient');
        if (modelVariantLower.includes('70b') === true) {
          speed = t('llmSelector.features.speedValues.medium');
          performance = t('llmSelector.features.speedValues.excellent');
        } else {
          speed = t('llmSelector.features.speedValues.fast');
          performance = t('llmSelector.features.speedValues.good');
        }
      } else if (modelVariantLower.includes('gemini') === true && modelVariantLower.includes('pro') === true) {
        description = t('llmSelector.descriptions.googleFlagship');
        speed = t('llmSelector.features.speedValues.medium');
        performance = t('llmSelector.features.speedValues.excellent');
        features.push({ name: t('llmSelector.features.multimodal'), value: t('llmSelector.features.yes') });
      } else if ((modelVariantLower.includes('gemini') === true && modelVariantLower.includes('flash') === true) || (modelIdLower.includes('gemini-3-flash') === true)) {
        description = t('llmSelector.descriptions.fastCostEffectiveGemini');
        speed = t('llmSelector.features.speedValues.veryFast');
        performance = t('llmSelector.features.speedValues.good');
        if (modelVariantLower.includes('lite') === true) {
          description = t('llmSelector.descriptions.moreLightweightGeminiFlash');
          speed = t('llmSelector.features.speedValues.ultraFast');
          performance = t('llmSelector.features.speedValues.fair');
        }
      }
      features.unshift({ name: t('llmSelector.features.speed'), value: speed });
      features.unshift({ name: t('llmSelector.features.performance'), value: performance });
    }
    const enriched = {
      ...model,
      displayName: autoModel === true ? t('llmSelector.autoLabel', 'Auto') : getModelDisplayName(model),
      description,
      icon: autoModel === true ? 'mdi:robot-happy-outline' : icon,
      iconType: autoModel !== true && providerLower === 'groq' ? 'asset' : 'iconify',
      features
    };
    if (autoModel === true && enriched.__key === undefined) {
      enriched.__key = 'auto-llm-option';
    }
    return enriched;
  });

  const enrichedList = [...mapped];
  const hasAutoModel = enrichedList.some((model) => isAutoModel(model) !== false);
  if (hasAutoModel === false) {
    enrichedList.unshift({
      id: autoLlmModelId,
      provider: 'Auto',
      product: 'Auto',
      productVariant: 'Auto',
      productVersion: '',
      displayName: t('llmSelector.autoLabel', 'Auto'),
      description: t('llmSelector.autoDescription', 'The platform will choose the best AI (LLM) for your agent'),
      icon: 'carbon:magic-wand',
      features: [
        { name: t('llmSelector.features.performance'), value: t('llmSelector.features.speedValues.good') },
        { name: t('llmSelector.features.speed'), value: t('llmSelector.features.speedValues.medium') },
        { name: t('llmSelector.features.toolSupport'), value: t('llmSelector.features.yes') },
        { name: t('llmSelector.features.voiceAgent'), value: t('llmSelector.features.yes') }
      ],
      __key: 'auto-llm-option',
      isAuto: true
    });
  }

  enrichedList.sort((first, second) => {
    if (isAutoModel(first) === isAutoModel(second)) return 0;
    return isAutoModel(first) === true ? -1 : 1;
  });
  return enrichedList;
});

const autoLinkedModelIds = computed(() => {
  const autoModel = enrichedModels.value.find(model => isAutoModel(model) === true);
  if (autoModel === undefined || autoModel === null) {
    return [];
  }
  const linked = [];
  const candidateFields = ['fastModeModelId','superFastModeModelId','autoAnswerModelId','autoVerificationModelId','autoQueryModelId','cheapResponsesLLM','cheapResponsesLlmModelId'];
  for (let i = 0; i < candidateFields.length; i = i + 1) {
    const fieldName = candidateFields[i];
    const rawValue = typeof autoModel[fieldName] === 'string' ? autoModel[fieldName].trim() : '';
    if (rawValue.length > 0 && linked.includes(rawValue) === false) {
      linked.push(rawValue);
    }
  }
  return linked;
});

const filteredModels = computed(() => {
  if (searchQuery.value.trim() === '') {
    return enrichedModels.value;
  }
  const lowerCaseQuery = searchQuery.value.toLowerCase().trim();
  return enrichedModels.value.filter(model => {
    const displayName = (model.displayName !== undefined && model.displayName !== null && model.displayName !== '') ? model.displayName.toLowerCase() : '';
    const description = (model.description !== undefined && model.description !== null && model.description !== '') ? model.description.toLowerCase() : '';
    const provider = (model.provider !== undefined && model.provider !== null && model.provider !== '') ? model.provider.toLowerCase() : '';
    const variant = (model.productVariant !== undefined && model.productVariant !== null && model.productVariant !== '') ? model.productVariant.toLowerCase() : '';

    if (
      displayName.includes(lowerCaseQuery) === true ||
      description.includes(lowerCaseQuery) === true ||
      provider.includes(lowerCaseQuery) === true ||
      variant.includes(lowerCaseQuery) === true
    ) {
      return true;
    }
    return false;
  });
});

const selectedModelObject = computed(() => {
  if (props.selectedLLMModel === null) {
    return null;
  }
  return enrichedModels.value.find(model => model.id === props.selectedLLMModel) != null ? enrichedModels.value.find(model => model.id === props.selectedLLMModel) : null;
});

const toggleDropdown = () => {
  if (props.locked === true) {
    isOpen.value = false;
    searchQuery.value = '';
    return;
  }
  isOpen.value = !isOpen.value;
  if (isOpen.value === false) {
    searchQuery.value = '';
  }
};

const selectModelAndClose = (modelId) => {
  emit('update:selectedLLMModel', modelId);
  isOpen.value = false;
  searchQuery.value = '';
};

const handleClickOutside = (event) => {
  if (dropdownListRef.value !== null && isOpen.value === true && dropdownListRef.value.contains(event.target) === false && event.target.closest('.llm-selector-dropdown-container button[aria-haspopup="listbox"]') === null) {
    isOpen.value = false;
    searchQuery.value = '';
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside, true);
});

function isModelSelected(model) {
  if (isAutoModel(model) === true) {
    if (props.selectedLLMModel === autoLlmModelId) {
      return true;
    }
    if (autoLinkedModelIds.value.includes(props.selectedLLMModel) === true) {
      return true;
    }
    return false;
  }
  return model.id === props.selectedLLMModel;
}

</script>

<style scoped>
.llm-options-list {
  max-height: 400px; 
  overflow-y: auto;
}



.selected-llm-head > span:first-child { margin-right: 12px; }
.llm-option-row > div:first-child { margin-right: 16px; }
.selected-llm-head { gap: 0; }
.llm-option-row { gap: 0; }
.llm-selector-dropdown-container[dir='rtl'] .selected-llm-head > span:first-child { margin-right: 0; margin-left: 12px; }
.llm-selector-dropdown-container[dir='rtl'] .llm-option-row > div:first-child { margin-right: 0; margin-left: 16px; }
</style> 